import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Checkbox, ContextMenu, Item, MenuButton, WithCombo } from 'basic-components';
import { ArrowDown, ArrowUp } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';
import { ReduxService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import AddCurveDatapointModal from '../../App/Authed/Curves/AddCurveDatapointModal';
import AddSeriesDatapointModal from '../../App/Authed/Series/AddSeriesDatapointModal';
import PatternItem from './PatternItem';

import { Else, If, Then, When } from 'react-if';

import classNames from 'classnames';
import './BasketHeader.scss';

const useAnimatedCounter = selectedItemsLength => {
  const [isAnimationActive, setIsAnimationActive] = useState(false),
        [symbolsCount, setSymbolsCount] = useState(0),
        [nextSymbolsCount, setNextSymbolsCount] = useState(),
        symbolsCountAnimation = useRef(),
        turnOffAnimation = useRef()

  useEffect(() => {
    setNextSymbolsCount(selectedItemsLength)
    setIsAnimationActive(true)
    clearTimeout(symbolsCountAnimation.current)
    symbolsCountAnimation.current = setTimeout(() => setSymbolsCount(selectedItemsLength), 250)
    clearTimeout(turnOffAnimation.current)
    turnOffAnimation.current = setTimeout(() => setIsAnimationActive(false), 500)
  }, [selectedItemsLength])

  useEffect(() => () => {
    clearTimeout(symbolsCountAnimation.current)
    clearTimeout(symbolsCountAnimation.current)
  }, [])

  return { symbolsCount, nextSymbolsCount, isAnimationActive }
}


const emptyFun = () => {}
const MAX = 20
const EMPTY_ARRAY = []

const BasketHeader = ({ addToSheet, counterLabel = "selected", onClearSelected, onConfigure, onReset, totalSize = 0, symbols = EMPTY_ARRAY, selectedItems, isStartingResults = false, selectedColumns, withPattern = true, withSelect = true, onChangeColumns }) => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.localSource),
        userColumns = useSelector(state => state.settingsState.columns) || EMPTY_ARRAY,
        selectedItemsLength = selectedItems.length,
        areItemsSelected = useMemo(() => selectedItemsLength > 0, [selectedItemsLength]),
        targetSource = useMemo(() => {
          if (selectedItems?.[0]?.source) {
            return selectedItems[0].source
          } else {
            return source
          }
        }, [selectedItems, source]),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        [isAddSeriesDatapointModalOpen, setIsAddSeriesDatapointModalOpen] = useState(),
        [isAddCurveDatapointModalOpen, setIsAddCurveDatapointModalOpen] = useState(),
        swapIsOpen = useCallback(() => selectedItems[0]?.root ? setIsAddCurveDatapointModalOpen(open => !open) : setIsAddSeriesDatapointModalOpen(open => !open), [selectedItems]),
        onMenuChange = useCallback(({ open }) => setIsMenuOpen(open), []),
        onResetLocal = useCallback(() => {
          dispatch(globalActions.getToastsStore()).warning("All changes will be discarded. Do you want to proceed?", "Clear changes",{
            withCancel: false,
            confirmText: "Clear",
            confirmAction: () => {
              onReset()
            }
          })
        }, [dispatch, onReset]),
        areAllSelected = useMemo(() => selectedItemsLength > 0 && selectedItemsLength === totalSize, [selectedItemsLength, totalSize]),
        selectAll = useCallback(() => {
          let symbolsToSelect = []
          if(totalSize > MAX) {
            symbolsToSelect = symbols.slice(0, MAX)
          } else {
            symbolsToSelect = symbols
          }
          const symbolsToSelectLength = symbolsToSelect.length
          dispatch(globalActions.getMessageStore()).info(`We added ${(symbolsToSelectLength >= MAX ? `${MAX} first ` : symbolsToSelectLength)} items. ${(symbolsToSelectLength >= MAX ? " You might want to filter down the results." : "")}`)
          symbolsToSelect.forEach(symbol => {
            dispatch(creatorSeriesActions.swapSelectedSymbol(ReduxService.updateObject(symbol, { pattern: "false" })))
          });
        }, [dispatch, symbols, totalSize]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() =>
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withHeader("Target", targetSource, true)
        , [getApiConfigLocal, targetSource]),
        { data: columns = EMPTY_ARRAY } = dataStorageQueries.useGetColumns({ getQueryBuilder, selectedSymbols: selectedItems, provider: targetSource }),
        columnValues = useMemo(() => columns.map(column => column.value), [columns]),
        { symbolsCount, nextSymbolsCount, isAnimationActive } = useAnimatedCounter(selectedItemsLength)

  useEffect(() => {
    if(selectedItems.length > 0 && selectedColumns.length === 0 && columnValues.length > 0) {
      const finded = columnValues.find(val => userColumns.find(c => String(c).toLowerCase().trim() === String(val).toLowerCase().trim()))
      onChangeColumns([finded || columnValues[0]])
    }
  }, [columnValues, dispatch, onChangeColumns, selectedColumns, selectedItems, userColumns])
  return (
    <div className={classNames(
      "ng-office-app__authed__content__basket-header",
      { "ng-office-app__authed__content__basket-header--is-selected": areItemsSelected }
    )}>
      <div className="ng-office-app__authed__content__basket-header__left">
        <ContextMenu
            gap={8}
            itemsSize="unset"
            menuButton={(
              <MenuButton key="menuButtonBasketHeader">
                <div className={classNames(
                  "ng-office-app__authed__content__basket-header__left__menu",
                  { "ng-office-app__authed__content__basket-header__left__menu--is-selected": areItemsSelected }
                )}>
                  <Checkbox
                      checked={areAllSelected}
                      indeterminate={areItemsSelected}
                      onChange={emptyFun}
                      tabIndex={-1}
                  >
                    {""}
                  </Checkbox>
                <div className="ng-office-app__authed__content__basket-header__left__menu__counter">
                  <div className={classNames(
                    "ng-office-app__authed__content__basket-header__left__menu__counter__carousel",
                    { "ng-office-app__authed__content__basket-header__left__menu__counter__carousel--is-animation-active": isAnimationActive }
                  )}>
                    <div className="ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value--is-new">
                      {nextSymbolsCount}
                    </div>
                    <div className="ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value ng-office-app__authed__content__basket-header__left__menu__counter__carousel__value--is-pernament">
                      {symbolsCount}
                    </div>
                  </div>
                  <div className="ng-office-app__authed__content__basket-header__left__menu__counter__label">
                    {counterLabel}
                  </div>
                  <div className="ng-office-app__authed__content__basket-header__left__menu__counter__arrow">
                    <If condition={isMenuOpen}>
                      <Then>
                        <ArrowUp
                            color={areItemsSelected ? "white-base" : "grey-dark"}
                            height={12}
                            isWithoutBorder
                            width={12}
                        />
                      </Then>
                      <Else>
                        <ArrowDown
                            color={areItemsSelected ? "white-base" : "grey-dark"}
                            height={12}
                            isWithoutBorder
                            width={12}
                        />
                      </Else>
                    </If>
                  </div>
                </div>
                </div>
              </MenuButton>
            )}
            onMenuChange={onMenuChange}
            shift={8}
        >
          <When condition={withSelect}>
            {() => (
              <Item
                  disabled={selectedItemsLength >= MAX || isStartingResults}
                  onClick={selectAll}
              >
                Select all
              </Item>
            )}
          </When>
          <Item
              disabled={selectedItemsLength === 0}
              onClick={onClearSelected}
          >
            Clear selected
          </Item>
          <When condition={withPattern}>
            {() => (
              <PatternItem
                  isStartingResults={isStartingResults}
                  selectedItems={selectedItems}
              />
            )}
          </When>
          <Item onClick={onResetLocal}>
            Clear and reset
          </Item>
        </ContextMenu>
      </div>
      <div className="ng-office-app__authed__content__basket-header__right">
        <div className="ng-office-app__authed__content__basket-header__right__button-container">
          <Button
              disabled={!areItemsSelected}
              onClick={onConfigure}
              size="small"
              variant="secondary"
          >
            Configure
          </Button>
        </div>
        <WithCombo
            items={
              <>
                <Item onClick={addToSheet}>
                  Insert all data
                </Item>
                <Item onClick={swapIsOpen}>
                  Insert single data point
                </Item>
              </>
            }
        >
          <Button
              disabled={!areItemsSelected || selectedColumns.length === 0}
              onClick={addToSheet}
              size="small"
              variant="primary"
          >
            {areItemsSelected && selectedColumns.length === 0 ? "Loading.." : "Insert data"}
          </Button>
        </WithCombo>
        <When condition={isAddSeriesDatapointModalOpen}>
          {() => (
            <AddSeriesDatapointModal 
                onClose={swapIsOpen}
            />
          )}
        </When> 
        <When condition={isAddCurveDatapointModalOpen}>
          {() => (
            <AddCurveDatapointModal 
                onClose={swapIsOpen}
            />
          )}
        </When> 
      </div>
    </div>
  )
}

BasketHeader.propTypes = {
  addToSheet: PropTypes.func.isRequired,
  counterLabel: PropTypes.string,
  isStartingResults: PropTypes.bool,
  onChangeColumns: PropTypes.func.isRequired,
  onClearSelected: PropTypes.func.isRequired,
  onConfigure: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  selectedColumns: PropTypes.array.isRequired,
  selectedItems: PropTypes.array.isRequired,
  symbols: PropTypes.array,
  totalSize: PropTypes.number,
  withPattern: PropTypes.bool,
  withSelect: PropTypes.bool,
}

export default BasketHeader;
